import React, { useState } from 'react';
import {
  Upload, Loader2, Save, Store, Hash, Tag, MessageSquare,
  Phone, MessageCircle, Mail, Globe, Image as ImageIcon, FileImage, MapPin,
  Eye, EyeOff, Trash2, ExternalLink, CheckCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { getThumbnailUrl } from '../../lib/imageUtils';
import { Separator } from '../ui/separator';
import CheckboxListWithSearch from '../shared/CheckboxListWithSearch';

export const VendorStallTab = ({
  stall, setStall,
  hasStall,
  categories, markets,
  selectedMarkets, handleMarketToggle,
  marketLocations, setMarketLocations, updateMarketLocation,
  availableTags, selectedTags, setSelectedTags, handleTagToggle,
  saving, uploading, selectedFiles,
  showPreview, setShowPreview,
  handleImageUpload, handleDeleteImage,
  handleGalleryUpload, handleGalleryDelete,
  handleCreateStall, handleUpdate,
  newStall, setNewStall, newStallFiles, setNewStallFiles,
  vendorType = 'market',
  vendorTypes = null,
  marketTypeMap = {},
  pendingTypeRequests = [],
  onRequestTypeAccess = null,
}) => {
  // Fallbacks for callers that haven't been updated yet
  const effectiveVendorTypes = vendorTypes && vendorTypes.length > 0 ? vendorTypes : [vendorType];
  const allTypes = ['market', 'expo', 'festival'];

  // Which type-tab is currently active in the create/edit stall Locations section
  const [activeLocationTab, setActiveLocationTab] = useState(vendorType);

  // Dynamic labels based on vendor type
  const locationLabel = vendorType === 'expo' ? 'Expos & Locations'
    : vendorType === 'festival' ? 'Festivals & Locations'
    : 'Markets & Locations';
  const locationSingular = vendorType === 'expo' ? 'Expo'
    : vendorType === 'festival' ? 'Festival'
    : 'Market';
  const locationPlural = vendorType === 'expo' ? 'expos'
    : vendorType === 'festival' ? 'festivals'
    : 'markets';

  // Human labels for each type
  const typeLabels = {
    market: { plural: 'Markets', singular: 'Market' },
    expo: { plural: 'Expos', singular: 'Expo' },
    festival: { plural: 'Festivals', singular: 'Festival' },
  };

  // Reusable locked-access panel — shown for each vendor type the vendor doesn't have access to
  const LockedTypeAccessPanel = ({ typeKey }) => {
    const { plural, singular } = typeLabels[typeKey];
    const isPending = pendingTypeRequests.includes(typeKey);
    return (
      <div
        className="bg-gray-100 dark:bg-[#1a1a1a] border-2 border-dashed border-gray-300 dark:border-gray-700 p-5 rounded-xl opacity-90"
        data-testid={`locked-type-panel-${typeKey}`}
      >
        <div className="flex items-start gap-3">
          <MapPin className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-gray-600 dark:text-gray-400 mb-1">
              {plural} & Locations{' '}
              <span className="text-xs font-normal text-gray-500">(locked)</span>
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-500 mb-3">
              You currently don't have access to add {plural.toLowerCase()} to your stall. Request access from the admin to enable this.
            </p>
            {isPending ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 rounded-full text-sm font-medium">
                <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                Access requested — awaiting admin approval
              </div>
            ) : (
              <Button
                type="button"
                onClick={() => onRequestTypeAccess && onRequestTypeAccess(typeKey)}
                variant="outline"
                data-testid={`request-access-${typeKey}`}
                className="rounded-full border-[#FFD12D] text-[#252525] dark:text-white hover:bg-[#FFD12D] hover:text-[#252525]"
              >
                Request Access to {singular}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Reusable tab bar with 3 pills: Markets / Expos / Festivals
  const LocationTypeTabsBar = () => (
    <div className="flex flex-wrap gap-2 border-b-2 border-gray-200 dark:border-gray-700 pb-3" data-testid="location-type-tabs">
      {allTypes.map(t => {
        const isActive = activeLocationTab === t;
        const hasAccess = effectiveVendorTypes.includes(t);
        return (
          <button
            key={t}
            type="button"
            onClick={() => setActiveLocationTab(t)}
            data-testid={`location-tab-${t}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${
              isActive
                ? 'bg-[#FFD12D] text-[#252525] shadow-md'
                : hasAccess
                  ? 'bg-gray-100 dark:bg-[#1a1a1a] text-[#252525] dark:text-white hover:bg-gray-200 dark:hover:bg-[#252525]'
                  : 'bg-gray-100 dark:bg-[#1a1a1a] text-gray-400 dark:text-gray-500'
            }`}
          >
            <MapPin className={`w-4 h-4 ${isActive ? 'text-[#252525]' : hasAccess ? 'text-[#FFD12D]' : 'text-gray-400'}`} />
            {typeLabels[t].plural}
            {!hasAccess && <span className="text-[10px] uppercase tracking-wider">Locked</span>}
          </button>
        );
      })}
    </div>
  );

  return (
    <>
                {!hasStall ? (
                  /* Create New Stall Form - shown when vendor has no stall */
                  <div className="space-y-6">
                    <div className="bg-[#FFD12D]/20 border-2 border-[#FFD12D] p-6 rounded-xl">
                      <h3 className="text-xl font-bold text-[#252525] dark:text-white mb-2 flex items-center gap-2">
                        <Store className="w-6 h-6 text-[#FFD12D]" />
                        Create Your Stall
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        You don't have a stall yet. Fill in the details below to create your stall and start selling!
                      </p>
                    </div>

                    {/* Images */}
                    <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                      <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                        <ImageIcon className="w-5 h-5 text-[#FFD12D]" />
                        Images
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white">Logo</Label>
                          {newStallFiles.logo && (
                            <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-[#FFD12D] mb-2">
                              <img src={URL.createObjectURL(newStallFiles.logo)} alt="Logo preview" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <Input
                            type="file"
                            accept=".png,.jpg,.jpeg,.webp"
                            onChange={(e) => setNewStallFiles({...newStallFiles, logo: e.target.files[0]})}
                            className="dark:bg-[#1a1a1a] dark:text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white">Banner</Label>
                          {newStallFiles.banner && (
                            <div className="w-full h-24 rounded-lg overflow-hidden border-2 border-[#FFD12D] mb-2">
                              <img src={URL.createObjectURL(newStallFiles.banner)} alt="Banner preview" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <Input
                            type="file"
                            accept=".png,.jpg,.jpeg,.webp"
                            onChange={(e) => setNewStallFiles({...newStallFiles, banner: e.target.files[0]})}
                            className="dark:bg-[#1a1a1a] dark:text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Basic Info */}
                    <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                      <h3 className="text-lg font-bold text-[#252525] dark:text-white">Basic Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white">Company/Stall Name <span className="text-red-500">*</span></Label>
                          <Input
                            value={newStall.stallName}
                            onChange={(e) => setNewStall({...newStall, stallName: e.target.value})}
                            placeholder="Your business name"
                            className="dark:bg-[#1a1a1a] dark:text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white">Category <span className="text-red-500">*</span></Label>
                          <Select
                            value={newStall.category}
                            onValueChange={(value) => setNewStall({...newStall, category: value})}
                          >
                            <SelectTrigger className="dark:bg-[#1a1a1a] dark:text-white">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((cat) => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Tags - before Slogan */}
                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <Tag className="w-4 h-4 text-[#FFD12D]" />
                          Tags <span className="text-gray-400 text-sm">(Select up to 10)</span>
                        </Label>
                        <CheckboxListWithSearch
                          items={availableTags}
                          selected={selectedTags}
                          onToggle={(tag) => {
                            if (selectedTags.includes(tag)) {
                              setSelectedTags(selectedTags.filter(t => t !== tag));
                            } else if (selectedTags.length < 10) {
                              setSelectedTags([...selectedTags, tag]);
                            }
                          }}
                          maxSelected={10}
                          Icon={Tag}
                          testId="vendor-tags-list"
                          searchPlaceholder="Search tags..."
                          emptyText="No tags yet"
                        />
                        <p className="text-xs text-gray-500">{selectedTags.length}/10 tags selected</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white">Slogan <span className="text-red-500">*</span></Label>
                        <Input
                          value={newStall.slogan}
                          onChange={(e) => setNewStall({...newStall, slogan: e.target.value})}
                          placeholder="Your catchy slogan"
                          className="dark:bg-[#1a1a1a] dark:text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white">About <span className="text-red-500">*</span></Label>
                        <Textarea
                          value={newStall.about}
                          onChange={(e) => setNewStall({...newStall, about: e.target.value})}
                          placeholder="Tell customers about your business"
                          className="dark:bg-[#1a1a1a] dark:text-white min-h-[100px]"
                        />
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                      <h3 className="text-lg font-bold text-[#252525] dark:text-white">Contact Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white">Phone <span className="text-red-500">*</span></Label>
                          <Input
                            value={newStall.phone}
                            onChange={(e) => setNewStall({...newStall, phone: e.target.value})}
                            placeholder="+27 12 345 6789"
                            className="dark:bg-[#1a1a1a] dark:text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white">WhatsApp</Label>
                          <Input
                            value={newStall.whatsapp}
                            onChange={(e) => setNewStall({...newStall, whatsapp: e.target.value})}
                            placeholder="+27 12 345 6789"
                            className="dark:bg-[#1a1a1a] dark:text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white">Email <span className="text-red-500">*</span></Label>
                          <Input
                            type="email"
                            value={newStall.email}
                            onChange={(e) => setNewStall({...newStall, email: e.target.value})}
                            placeholder="your@email.com"
                            className="dark:bg-[#1a1a1a] dark:text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#252525] dark:text-white">Website</Label>
                          <Input
                            value={newStall.website}
                            onChange={(e) => setNewStall({...newStall, website: e.target.value})}
                            placeholder="https://www.yoursite.com"
                            className="dark:bg-[#1a1a1a] dark:text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Locations — tabbed by vendor type */}
                    <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                      <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-[#FFD12D]" />
                        {typeLabels[activeLocationTab].plural} & Locations <span className="text-red-500">*</span>
                      </h3>
                      <LocationTypeTabsBar />
                      {effectiveVendorTypes.includes(activeLocationTab) ? (
                        <>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Select at least one {typeLabels[activeLocationTab].singular.toLowerCase()}
                          </p>
                          <CheckboxListWithSearch
                            items={markets.filter(name => (marketTypeMap[name] || vendorType) === activeLocationTab)}
                            selected={selectedMarkets.filter(name => (marketTypeMap[name] || vendorType) === activeLocationTab)}
                            onToggle={handleMarketToggle}
                            Icon={MapPin}
                            testId={`vendor-markets-list-${activeLocationTab}`}
                            searchPlaceholder={`Search ${typeLabels[activeLocationTab].plural.toLowerCase()}...`}
                            emptyText={`No ${typeLabels[activeLocationTab].plural.toLowerCase()} available`}
                          />
                          {selectedMarkets.filter(name => (marketTypeMap[name] || vendorType) === activeLocationTab).map((market) => (
                            <div key={market} className="bg-white dark:bg-[#1a1a1a] p-4 rounded-lg border-2 border-[#FFD12D]/30 space-y-3 mt-4">
                              <h4 className="font-bold text-[#252525] dark:text-white flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-[#FFD12D]" />
                                {market}
                              </h4>
                              <div className="space-y-2">
                                <Label className="text-[#252525] dark:text-white">Stall Number <span className="text-red-500">*</span></Label>
                                <Input
                                  value={marketLocations[market]?.stallNumber || ''}
                                  onChange={(e) => setMarketLocations({
                                    ...marketLocations,
                                    [market]: { ...marketLocations[market], stallNumber: e.target.value }
                                  })}
                                  placeholder="e.g., A-12"
                                  className="dark:bg-[#252525] dark:text-white"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label className="text-[#252525] dark:text-white">Latitude</Label>
                                  <Input
                                    type="number"
                                    step="0.000001"
                                    value={marketLocations[market]?.lat || ''}
                                    onChange={(e) => setMarketLocations({
                                      ...marketLocations,
                                      [market]: { ...marketLocations[market], lat: e.target.value }
                                    })}
                                    placeholder="-25.7461"
                                    className="dark:bg-[#252525] dark:text-white"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-[#252525] dark:text-white">Longitude</Label>
                                  <Input
                                    type="number"
                                    step="0.000001"
                                    value={marketLocations[market]?.lng || ''}
                                    onChange={(e) => setMarketLocations({
                                      ...marketLocations,
                                      [market]: { ...marketLocations[market], lng: e.target.value }
                                    })}
                                    placeholder="28.1881"
                                    className="dark:bg-[#252525] dark:text-white"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        <LockedTypeAccessPanel typeKey={activeLocationTab} />
                      )}
                    </div>

                    {/* Status */}
                    <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                      <h3 className="text-lg font-bold text-[#252525] dark:text-white">Stall Visibility</h3>
                      <Select
                        value={newStall.status}
                        onValueChange={(value) => setNewStall({...newStall, status: value})}
                      >
                        <SelectTrigger className="dark:bg-[#1a1a1a] dark:text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">
                            <span className="flex items-center gap-2">
                              <EyeOff className="w-4 h-4" /> Draft (Hidden)
                            </span>
                          </SelectItem>
                          <SelectItem value="published">
                            <span className="flex items-center gap-2">
                              <Eye className="w-4 h-4" /> Published (Visible)
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Create Button */}
                    <Button
                      onClick={handleCreateStall}
                      disabled={saving}
                      className="w-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-bold py-4"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Creating Stall...
                        </>
                      ) : (
                        <>
                          <Store className="w-5 h-5 mr-2" />
                          Create My Stall
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                /* Existing Stall Edit Form */
                <form onSubmit={handleUpdate} className="space-y-6">
                  {/* Images Section */}
                  <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                    <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                      <ImageIcon className="w-5 h-5 text-[#FFD12D]" />
                      Images
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <FileImage className="w-4 h-4 text-[#FFD12D]" />
                          Logo <span className="text-red-500">*</span>
                        </Label>
                        
                        {/* Logo Preview Area */}
                        <div className="flex items-start gap-4">
                          <div className={`relative w-28 h-28 rounded-xl overflow-hidden flex-shrink-0 ${
                            stall?.logo 
                              ? 'border-4 border-[#FFD12D]' 
                              : 'border-4 border-dashed border-red-300 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
                          }`}>
                            {stall?.logo ? (
                              <img
                                src={getThumbnailUrl(stall.logo)}
                                alt="Logo"
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center">
                                <FileImage className="w-8 h-8 text-red-400 mb-1" />
                                <span className="text-xs text-red-500 text-center px-2">Required</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 space-y-3">
                            {/* Upload Button - More prominent */}
                            <label className="block cursor-pointer">
                              <input
                                type="file"
                                accept=".png,.jpg,.jpeg,.webp"
                                onChange={(e) => handleImageUpload('logo', e.target.files[0])}
                                disabled={uploading.logo}
                                className="hidden"
                                data-testid="logo-upload-input"
                              />
                              <Button
                                type="button"
                                className={`w-full h-11 font-semibold ${
                                  uploading.logo 
                                    ? 'bg-gray-300 cursor-not-allowed' 
                                    : 'bg-[#FFD12D] hover:bg-[#e6bc29] text-[#252525]'
                                }`}
                                disabled={uploading.logo}
                                asChild
                              >
                                <span className="flex items-center justify-center gap-2">
                                  {uploading.logo ? (
                                    <>
                                      <Loader2 className="w-5 h-5 animate-spin" />
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-5 h-5" />
                                      {stall?.logo ? 'Change Logo' : 'Upload Logo'}
                                    </>
                                  )}
                                </span>
                              </Button>
                            </label>
                            
                            {/* Selected file indicator */}
                            {selectedFiles.logo && (
                              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-green-700 dark:text-green-300 truncate flex-1">
                                  {selectedFiles.logo}
                                </span>
                              </div>
                            )}
                            
                            {/* Delete button */}
                            {stall?.logo && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => handleDeleteImage('logo')}
                                className="w-full h-9 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                                data-testid="delete-logo-btn"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remove Logo
                              </Button>
                            )}
                            
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              PNG, JPG, JPEG, WEBP • Max 3MB • Recommended: 1080×1080px
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <FileImage className="w-4 h-4 text-[#FFD12D]" />
                          Banner <span className="text-red-500">*</span>
                        </Label>
                        
                        {/* Banner Preview Area */}
                        <div className={`relative w-full h-36 rounded-xl overflow-hidden ${
                          stall?.banner 
                            ? 'border-4 border-[#FFD12D]' 
                            : 'border-4 border-dashed border-red-300 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
                        }`}>
                          {stall?.banner ? (
                            <img
                              src={getThumbnailUrl(stall.banner)}
                              alt="Banner"
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center">
                              <FileImage className="w-10 h-10 text-red-400 mb-2" />
                              <span className="text-sm text-red-500">Banner image required</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-col sm:flex-row gap-3">
                          {/* Upload Button - More prominent */}
                          <label className="flex-1 cursor-pointer">
                            <input
                              type="file"
                              accept=".png,.jpg,.jpeg,.webp"
                              onChange={(e) => handleImageUpload('banner', e.target.files[0])}
                              disabled={uploading.banner}
                              className="hidden"
                              data-testid="banner-upload-input"
                            />
                            <Button
                              type="button"
                              className={`w-full h-11 font-semibold ${
                                uploading.banner 
                                  ? 'bg-gray-300 cursor-not-allowed' 
                                  : 'bg-[#FFD12D] hover:bg-[#e6bc29] text-[#252525]'
                              }`}
                              disabled={uploading.banner}
                              asChild
                            >
                              <span className="flex items-center justify-center gap-2">
                                {uploading.banner ? (
                                  <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Uploading...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-5 h-5" />
                                    {stall?.banner ? 'Change Banner' : 'Upload Banner'}
                                  </>
                                )}
                              </span>
                            </Button>
                          </label>
                          
                          {/* Delete button */}
                          {stall?.banner && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleDeleteImage('banner')}
                              className="h-11 px-4 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                              data-testid="delete-banner-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remove
                            </Button>
                          )}
                        </div>
                        
                        {/* Selected file indicator */}
                        {selectedFiles.banner && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-green-700 dark:text-green-300 truncate">
                              {selectedFiles.banner}
                            </span>
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          PNG, JPG, JPEG, WEBP • Max 3MB • Recommended: 2400×800px
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-gray-200 dark:bg-gray-700" />

                  {/* Gallery Media Section */}
                  <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                    <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                      <ImageIcon className="w-5 h-5 text-[#FFD12D]" />
                      Gallery Media
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Upload up to 10 photos (max 2MB each) to showcase your products and stall</p>

                    {/* Existing gallery images */}
                    {stall?.gallery && stall.gallery.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {stall.gallery.map((img, idx) => (
                          <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border-2 border-dashed border-gray-300 dark:border-gray-600">
                            <img
                              src={getThumbnailUrl(img)}
                              alt={`Gallery ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">{idx + 1}</div>
                            <button
                              type="button"
                              onClick={() => handleGalleryDelete(idx)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`gallery-delete-${idx}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload button */}
                    {(!stall?.gallery || stall.gallery.length < 10) && (
                      <div className="flex items-center gap-3">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                            className="hidden"
                            onChange={(e) => handleGalleryUpload(e.target.files[0])}
                            disabled={uploading?.gallery}
                            data-testid="gallery-upload-input"
                          />
                          <Button
                            type="button"
                            asChild
                            className={`h-11 px-6 ${uploading?.gallery ? 'opacity-50' : ''} bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] font-semibold`}
                            disabled={uploading?.gallery}
                          >
                            <span>
                              {uploading?.gallery ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                              ) : (
                                <><Upload className="w-4 h-4 mr-2" /> Add Photo</>
                              )}
                            </span>
                          </Button>
                        </label>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {stall?.gallery?.length || 0}/10 photos
                        </span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      PNG, JPG, JPEG, WEBP • Max 2MB per photo
                    </p>
                  </div>

                  <Separator className="bg-gray-200 dark:bg-gray-700" />

                  {/* Basic Information Section */}
                  <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                    <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                      <Store className="w-5 h-5 text-[#FFD12D]" />
                      Basic Information
                    </h3>
                    
                    <div className="space-y-2">
                      <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                        <Store className="w-4 h-4 text-[#FFD12D]" />
                        Company Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={stall?.stallName || ''}
                        onChange={(e) => setStall({ ...stall, stallName: e.target.value })}
                        placeholder="e.g., Fresh Farm Produce"
                        className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                        <Tag className="w-4 h-4 text-[#FFD12D]" />
                        Category <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={stall?.category || ''}
                        onValueChange={(value) => setStall({ ...stall, category: value })}
                      >
                        <SelectTrigger className="dark:bg-[#1a1a1a] dark:text-white">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#252525] dark:text-white flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-[#FFD12D]" />
                          Tags <span className="text-gray-400 text-sm">(Select up to 10)</span>
                        </span>
                        <span className="text-xs text-gray-500">
                          {selectedTags.length}/10 selected
                        </span>
                      </Label>
                      <CheckboxListWithSearch
                        items={availableTags}
                        selected={selectedTags}
                        onToggle={handleTagToggle}
                        maxSelected={10}
                        Icon={Tag}
                        testId="vendor-tags-list-edit"
                        searchPlaceholder="Search tags..."
                        emptyText="No tags yet"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-[#FFD12D]" />
                        Slogan <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={stall?.slogan || ''}
                        onChange={(e) => setStall({ ...stall, slogan: e.target.value })}
                        placeholder="e.g., Farm Fresh, Daily Delivered"
                        className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#252525] dark:text-white flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-[#FFD12D]" />
                          About <span className="text-red-500">*</span>
                        </span>
                        <span className="text-xs text-gray-500">
                          {stall?.about?.length || 0}/250
                        </span>
                      </Label>
                      <Textarea
                        value={stall?.about || ''}
                        onChange={(e) => {
                          if (e.target.value.length <= 250) {
                            setStall({ ...stall, about: e.target.value });
                          }
                        }}
                        placeholder="Tell customers about your business (max 250 characters)"
                        maxLength={250}
                        rows={4}
                        className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                      />
                    </div>
                  </div>

                  <Separator className="bg-gray-200 dark:bg-gray-700" />

                  {/* Contact Information Section */}
                  <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                    <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                      <Phone className="w-5 h-5 text-[#FFD12D]" />
                      Contact Information
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <Phone className="w-4 h-4 text-[#FFD12D]" />
                          Phone <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={stall?.phone || ''}
                          onChange={(e) => setStall({ ...stall, phone: e.target.value })}
                          placeholder="+27 12 345 6789"
                          className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-[#FFD12D]" />
                          WhatsApp <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={stall?.whatsapp || ''}
                          onChange={(e) => setStall({ ...stall, whatsapp: e.target.value })}
                          placeholder="+27 12 345 6789"
                          className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#FFD12D]" />
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="email"
                        value={stall?.email || ''}
                        onChange={(e) => setStall({ ...stall, email: e.target.value })}
                        placeholder="contact@example.com"
                        className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                        <Globe className="w-4 h-4 text-[#FFD12D]" />
                        Website <span className="text-gray-400 text-sm">(Optional)</span>
                      </Label>
                      <Input
                        value={stall?.website || ''}
                        onChange={(e) => setStall({ ...stall, website: e.target.value })}
                        placeholder="https://www.yourwebsite.com"
                        className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                      />
                    </div>
                  </div>

                  <Separator className="bg-gray-200 dark:bg-gray-700" />

                  {/* Social Media Section */}
                  <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
                    <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
                      </svg>
                      Social Media
                      <span className="text-gray-400 text-sm font-normal ml-2">(Optional)</span>
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
                          </svg>
                          Facebook
                        </Label>
                        <Input
                          value={stall?.socialMedia?.facebook || ''}
                          onChange={(e) => setStall({ 
                            ...stall, 
                            socialMedia: { ...stall?.socialMedia, facebook: e.target.value } 
                          })}
                          placeholder="https://facebook.com/yourpage"
                          className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/>
                          </svg>
                          Instagram
                        </Label>
                        <Input
                          value={stall?.socialMedia?.instagram || ''}
                          onChange={(e) => setStall({ 
                            ...stall, 
                            socialMedia: { ...stall?.socialMedia, instagram: e.target.value } 
                          })}
                          placeholder="https://instagram.com/yourprofile"
                          className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                          </svg>
                          TikTok
                        </Label>
                        <Input
                          value={stall?.socialMedia?.tiktok || ''}
                          onChange={(e) => setStall({ 
                            ...stall, 
                            socialMedia: { ...stall?.socialMedia, tiktok: e.target.value } 
                          })}
                          placeholder="https://tiktok.com/@yourprofile"
                          className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                          X (Twitter)
                        </Label>
                        <Input
                          value={stall?.socialMedia?.twitter || ''}
                          onChange={(e) => setStall({ 
                            ...stall, 
                            socialMedia: { ...stall?.socialMedia, twitter: e.target.value } 
                          })}
                          placeholder="https://x.com/yourhandle"
                          className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-[#252525] dark:text-white flex items-center gap-2">
                          <svg className="w-4 h-4 text-[#FFD12D]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                          LinkedIn
                        </Label>
                        <Input
                          value={stall?.socialMedia?.linkedin || ''}
                          onChange={(e) => setStall({ 
                            ...stall, 
                            socialMedia: { ...stall?.socialMedia, linkedin: e.target.value } 
                          })}
                          placeholder="https://linkedin.com/in/yourprofile"
                          className="dark:bg-[#1a1a1a] dark:text-white placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-gray-200 dark:bg-gray-700" />

                  {/* Location Section — tabbed by vendor type */}
                  <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-6">
                    <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-[#FFD12D]" />
                      {typeLabels[activeLocationTab].plural} & Locations
                    </h3>

                    <LocationTypeTabsBar />

                    {effectiveVendorTypes.includes(activeLocationTab) ? (
                      <>
                        <div className="space-y-3">
                          <Label className="text-[#252525] dark:text-white font-semibold">
                            Select At Least One {typeLabels[activeLocationTab].singular} <span className="text-red-500">*</span>
                          </Label>
                          <CheckboxListWithSearch
                            items={markets.filter(name => (marketTypeMap[name] || vendorType) === activeLocationTab)}
                            selected={selectedMarkets.filter(name => (marketTypeMap[name] || vendorType) === activeLocationTab)}
                            onToggle={handleMarketToggle}
                            Icon={MapPin}
                            testId={`vendor-markets-list-edit-${activeLocationTab}`}
                            searchPlaceholder={`Search ${typeLabels[activeLocationTab].plural.toLowerCase()}...`}
                            emptyText={`No ${typeLabels[activeLocationTab].plural.toLowerCase()} available`}
                          />
                        </div>

                        {selectedMarkets.filter(name => (marketTypeMap[name] || vendorType) === activeLocationTab).map((market) => (
                          <div key={market} className="bg-white dark:bg-[#1a1a1a] p-5 rounded-lg border-2 border-[#FFD12D]/30 space-y-4">
                            <h5 className="font-bold text-[#252525] dark:text-white flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-[#FFD12D]" />
                              {market}
                            </h5>

                            <div className="space-y-2">
                              <Label className="text-[#252525] dark:text-white">
                                Stall Number <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                value={marketLocations[market]?.stallNumber || ''}
                                onChange={(e) => updateMarketLocation(market, 'stallNumber', e.target.value)}
                                placeholder="e.g., A-12"
                                className="dark:bg-[#252525] dark:text-white"
                              />
                            </div>

                            {/* GPS Coordinates Section */}
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <Label className="text-[#252525] dark:text-white font-medium">
                                  GPS Location <span className="text-red-500">*</span>
                                </Label>
                                <a
                                  href="https://www.google.com/maps"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Find on Google Maps
                                </a>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Right-click your stall location on Google Maps and select "What's here?" to get coordinates.
                              </p>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label className="text-[#252525] dark:text-white text-sm">
                                    Latitude (e.g., -26.2041)
                                  </Label>
                                  <Input
                                    type="number"
                                    step="0.000001"
                                    value={marketLocations[market]?.lat || ''}
                                    onChange={(e) => updateMarketLocation(market, 'lat', e.target.value)}
                                    placeholder="-26.204100"
                                    className="dark:bg-[#252525] dark:text-white"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-[#252525] dark:text-white text-sm">
                                    Longitude (e.g., 28.0473)
                                  </Label>
                                  <Input
                                    type="number"
                                    step="0.000001"
                                    value={marketLocations[market]?.lng || ''}
                                    onChange={(e) => updateMarketLocation(market, 'lng', e.target.value)}
                                    placeholder="28.047300"
                                    className="dark:bg-[#252525] dark:text-white"
                                  />
                                </div>
                              </div>
                              {marketLocations[market]?.lat && marketLocations[market]?.lng && (
                                <a
                                  href={`https://www.google.com/maps?q=${marketLocations[market].lat},${marketLocations[market].lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 hover:underline"
                                >
                                  <MapPin className="w-3 h-3" />
                                  Preview location on map
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <LockedTypeAccessPanel typeKey={activeLocationTab} />
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Preview Button - show when stall is in draft or for any vendor to preview */}
                    <Button 
                      type="button"
                      onClick={() => setShowPreview(true)}
                      variant="outline"
                      className="flex-1 sm:flex-none border-2 border-[#252525] dark:border-white text-[#252525] dark:text-white hover:bg-[#252525] hover:text-white dark:hover:bg-white dark:hover:text-[#252525] font-semibold py-6 transition-all duration-300"
                      data-testid="stall-preview-btn"
                    >
                      <Eye className="mr-2 h-5 w-5" />
                      Stall Preview
                    </Button>
                    
                    {/* Save Button */}
                    <Button 
                      type="submit" 
                      disabled={saving} 
                      className="flex-1 bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold py-6 transition-all duration-300"
                      data-testid="save-changes-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-5 w-5" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </form>
                )}

    </>
  );
};
